import { Link } from 'react-router-dom';
import { PLANS } from '@racksavant/ui';
import { cx } from '../cx';
import ui from '../ui.module.css';
import s from './Plans.module.css';

export type PlanId = 'free' | 'muse' | 'studio';

export interface PlanView {
  id: PlanId;
  name: string;
  price: string;
  period?: string;
  line: string;
  features: readonly string[];
}

/** The plan table, in display order. Content comes from `@racksavant/ui`. */
export const PLAN_VIEWS: PlanView[] = [
  { id: 'free', ...PLANS.free },
  { id: 'muse', ...PLANS.muse },
  { id: 'studio', ...PLANS.studio },
];

export const CTA_LABELS: Record<PlanId, string> = {
  free: 'Start free',
  muse: 'Become a Muse',
  studio: 'Go Studio',
};

/** Annual billing is twelve months for the price of ten. */
export const ANNUAL_MONTHS_CHARGED = 10;

function monthlyNumber(price: string): number | null {
  const value = Number(price.replace(/[^0-9.]/g, ''));
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** What the big number says, given the billing period. */
export function displayPrice(plan: PlanView, annual: boolean): string {
  const monthly = monthlyNumber(plan.price);
  if (monthly === null) return plan.price;
  if (!annual) return plan.price;
  const perMonth = (monthly * ANNUAL_MONTHS_CHARGED) / 12;
  const rounded = Math.round(perMonth * 100) / 100;
  return `$${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(2)}`;
}

/** The small line under it. */
export function billedNote(plan: PlanView, annual: boolean): string {
  const monthly = monthlyNumber(plan.price);
  if (monthly === null) return 'No card. No trial timer.';
  if (!annual) return 'Billed monthly. Cancel any time.';
  return `$${monthly * ANNUAL_MONTHS_CHARGED} billed yearly — two months free.`;
}

interface BillingToggleProps {
  annual: boolean;
  onChange: (annual: boolean) => void;
}

export function BillingToggle({ annual, onChange }: BillingToggleProps) {
  return (
    <div className={s.toggleWrap}>
      <div className={s.toggle} role="group" aria-label="Billing period">
        <button
          type="button"
          className={cx(s.toggleBtn, !annual && s.toggleBtnOn)}
          aria-pressed={!annual}
          onClick={() => onChange(false)}
        >
          Monthly
        </button>
        <button
          type="button"
          className={cx(s.toggleBtn, annual && s.toggleBtnOn)}
          aria-pressed={annual}
          onClick={() => onChange(true)}
        >
          Yearly
        </button>
      </div>
      <span className={s.savingNote}>Yearly gives you two months back.</span>
    </div>
  );
}

interface PlanCardProps {
  plan: PlanView;
  annual?: boolean;
  featured?: boolean;
  /** Trim the feature list — used for the landing page teaser. */
  maxFeatures?: number;
}

/**
 * A plan, set as a column in a rate card. Square, hairlines, the price in the
 * display serif at a size nothing else on the page is allowed to be.
 */
export function PlanCard({ plan, annual = false, featured = false, maxFeatures }: PlanCardProps) {
  const features = maxFeatures ? plan.features.slice(0, maxFeatures) : plan.features;

  return (
    <div className={cx(s.col, featured && s.featured)}>
      <span className={s.badge}>{featured ? 'Most chosen' : ' '}</span>

      <h3 className={s.name}>{plan.name}</h3>
      <p className={s.line}>{plan.line}</p>

      <p className={s.priceRow}>
        <span className={s.price}>{displayPrice(plan, annual)}</span>
        {plan.period ? <span className={s.period}>{annual ? '/mo' : plan.period}</span> : null}
      </p>
      <p className={s.billedNote}>{billedNote(plan, annual)}</p>

      <ul className={s.features}>
        {features.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>

      <Link to="/auth" className={cx(ui.btn, featured ? ui.primary : ui.ghost, s.cta)}>
        {CTA_LABELS[plan.id]}
      </Link>
    </div>
  );
}
